import {
  formatIsoDate,
  getIsoWeekNumber,
  getIsoWeekParity,
  getIsoWeekday,
  getMonthDates,
  getNthWeekdayOfMonth,
  isMonthListed,
  isSameOrAfterIsoDate,
  parseIsoDate,
  uniqueDates,
} from './calendar';
import { getSwedishHolidays } from './holidays';
import type {
  GenerateMonthlyOpsJobsInput,
  OpsGeneratedBlockInstance,
  OpsGeneratedJob,
  OpsGeneratedVisit,
  OpsIsoWeekday,
  OpsJobKind,
  OpsSite,
  OpsWorkBlock,
} from './types';

const JOB_KIND_BY_MODEL_TYPE: Record<OpsWorkBlock['model_type'], OpsJobKind> = {
  RECURRING_BLOCK: 'recurring',
  EXTRA_CLEANING_TEMPLATE: 'extra',
};

function resolveGeneratedAt(value: string | Date | undefined): string {
  if (!value) {
    return new Date().toISOString();
  }
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}

function resolveSitesAndBlocks(input: GenerateMonthlyOpsJobsInput): {
  sites: readonly OpsSite[];
  blocks: readonly OpsWorkBlock[];
  fixtureOrder: Map<string, number>;
  siteOrder: Map<string, number>;
  blockOrder: Map<string, number>;
} {
  if (input.fixtures && input.fixtures.length > 0) {
    const fixtureOrder = new Map<string, number>();
    const sites = input.fixtures.map((fixture, index) => {
      fixtureOrder.set(fixture.id, index);
      return {
        id: fixture.id,
        name: fixture.name,
        contract_path: fixture.contract_path,
        service_start_date: fixture.service_start_date,
        purpose: fixture.purpose,
        notes: fixture.notes,
        active: fixture.active,
      };
    });

    const blocks = input.fixtures.flatMap((fixture) => fixture.blocks);
    const siteOrder = new Map(sites.map((site, index) => [site.id, index]));
    const blockOrder = new Map(blocks.map((block, index) => [`${block.site_id}:${block.source_code}`, index]));

    return { sites, blocks, fixtureOrder, siteOrder, blockOrder };
  }

  if (!input.sites || !input.blocks) {
    throw new Error('generateMonthlyOpsJobs requires fixtures or both sites and blocks');
  }

  const sites = input.sites;
  const blocks = input.blocks;
  const fixtureOrder = new Map<string, number>();
  const siteOrder = new Map(sites.map((site, index) => [site.id, index]));
  const blockOrder = new Map(blocks.map((block, index) => [`${block.site_id}:${block.source_code}`, index]));

  return { sites, blocks, fixtureOrder, siteOrder, blockOrder };
}

function compareIsoDate(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function resolveJobDatesForBlock(block: OpsWorkBlock, year: number, month: number): Date[] {
  const monthDates = getMonthDates(year, month);
  const holidays = getSwedishHolidays(year);
  const isHoliday = (date: Date) => holidays.has(formatIsoDate(date));

  const candidateDates = monthDates.filter((date) => {
    const weekday = getIsoWeekday(date);
    return block.weekdays.includes(weekday);
  });

  let resolved: Date[];

  switch (block.cadence) {
    case 'WEEKLY':
      resolved = candidateDates;
      break;
    case 'BIWEEKLY':
      resolved = candidateDates.filter((date) => getIsoWeekParity(date) === block.parity);
      break;
    case 'MONTHLY':
    case 'EXACT_MONTHS': {
      if (block.week_in_month == null) {
        return [];
      }

      const dates: Date[] = [];
      for (const weekday of block.weekdays) {
        const candidate = getNthWeekdayOfMonth(year, month, weekday as OpsIsoWeekday, block.week_in_month);
        if (candidate) {
          dates.push(candidate);
        }
      }
      resolved = uniqueDates(dates);
      break;
    }
    case 'ONE_OFF': {
      if (!block.one_off_date) {
        return [];
      }
      const d = parseIsoDate(block.one_off_date);
      if (!d) {
        return [];
      }
      if (d.getFullYear() !== year || d.getMonth() + 1 !== month) {
        return [];
      }
      resolved = [d];
      break;
    }
    default:
      return [];
  }

  return resolved.filter((date) => !isHoliday(date));
}

function isBlockActiveForMonth(block: OpsWorkBlock, year: number, month: number): boolean {
  if (!block.active) {
    return false;
  }

  if (block.cadence === 'ONE_OFF') {
    if (!block.one_off_date) {
      return false;
    }
    const d = new Date(block.one_off_date + 'T00:00:00Z');
    return d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month;
  }

  if (block.cadence === 'EXACT_MONTHS') {
    return isMonthListed(block.exact_months, month);
  }

  return isMonthListed(block.active_months, month);
}

function makeJob(
  site: OpsSite,
  block: OpsWorkBlock,
  scheduledFor: Date,
  generatedAt: string,
  year: number,
  month: number,
): OpsGeneratedJob {
  const scheduledForIso = formatIsoDate(scheduledFor);
  const isoWeekday = getIsoWeekday(scheduledFor);
  const isoWeek = getIsoWeekNumber(scheduledFor);
  const jobKind = JOB_KIND_BY_MODEL_TYPE[block.model_type];

  return {
    job_key: `${site.id}:${block.source_code}:${scheduledForIso}`,
    site_id: site.id,
    site_name: site.name,
    block_id: block.id,
    block_source_code: block.source_code,
    block_label: block.label,
    block_area: block.area,
    job_kind: jobKind,
    scheduled_for: scheduledForIso,
    iso_weekday: isoWeekday,
    iso_week: isoWeek,
    status: 'planned',
    required_photos: block.required_photos,
    snapshot: {
      generated_at: generatedAt,
      year,
      month,
      site: {
        ...site,
      },
      work_block: {
        ...block,
      },
      schedule: {
        scheduled_for: scheduledForIso,
        iso_weekday: isoWeekday,
        iso_week: isoWeek,
        week_in_month: block.week_in_month,
        job_kind: jobKind,
      },
    },
  };
}

function sortJobs(a: OpsGeneratedJob, b: OpsGeneratedJob): number {
  return (
    compareIsoDate(a.scheduled_for, b.scheduled_for) ||
    a.site_id.localeCompare(b.site_id) ||
    a.snapshot.work_block.sort_order - b.snapshot.work_block.sort_order ||
    a.block_source_code.localeCompare(b.block_source_code) ||
    a.job_kind.localeCompare(b.job_kind)
  );
}

export function generateMonthlyOpsJobs(input: GenerateMonthlyOpsJobsInput): OpsGeneratedJob[] {
  const generatedAt = resolveGeneratedAt(input.generated_at);
  const { sites, blocks } = resolveSitesAndBlocks(input);
  const siteById = new Map(sites.map((site) => [site.id, site]));

  const jobs = blocks
    .filter((block) => isBlockActiveForMonth(block, input.year, input.month))
    .flatMap((block) => {
      const site = siteById.get(block.site_id);
      if (!site) {
        return [];
      }

      const dates = resolveJobDatesForBlock(block, input.year, input.month).filter((date) =>
        isSameOrAfterIsoDate(formatIsoDate(date), site.service_start_date),
      );

      const scheduledDates = block.first_visit_of_month ? dates.slice(0, 1) : dates;

      return scheduledDates.map((date) => makeJob(site, block, date, generatedAt, input.year, input.month));
    })
    .sort(sortJobs);

  return jobs;
}

function makeBlockInstance(job: OpsGeneratedJob, block: OpsWorkBlock): OpsGeneratedBlockInstance {
  return {
    block_key: `${block.id}:${job.scheduled_for}`,
    work_block_id: block.id,
    source_code: block.source_code,
    area: block.area,
    label: block.label,
    job_kind: job.snapshot.work_block.model_type === 'EXTRA_CLEANING_TEMPLATE' ? 'extra' : 'recurring',
    points: block.points ?? 0,
    structured_tasks: [...(block.structured_tasks ?? [])],
    sort_order: block.sort_order,
    required_photos: block.required_photos,
    snapshot: job.snapshot,
  };
}

export function generateMonthlyOpsVisits(input: GenerateMonthlyOpsJobsInput): OpsGeneratedVisit[] {
  const jobs = generateMonthlyOpsJobs(input);
  const { blocks } = resolveSitesAndBlocks(input);
  const blockById = new Map(blocks.map((block) => [block.id, block]));

  const visitMap = new Map<string, OpsGeneratedVisit>();

  for (const job of jobs) {
    const key = `${job.site_id}:${job.scheduled_for}`;

    if (!visitMap.has(key)) {
      visitMap.set(key, {
        visit_key: key,
        site_id: job.site_id,
        site_name: job.site_name,
        scheduled_for: job.scheduled_for,
        iso_weekday: job.iso_weekday,
        iso_week: job.iso_week,
        blocks: [],
        total_points: 0,
      });
    }

    const visit = visitMap.get(key)!;
    const block = blockById.get(job.block_id);

    if (block) {
      const instance = makeBlockInstance(job, block);
      visit.blocks.push(instance);
      visit.total_points += instance.points;
    }
  }

  for (const visit of visitMap.values()) {
    visit.blocks.sort((a, b) => a.sort_order - b.sort_order || a.source_code.localeCompare(b.source_code));
    visit.total_points = Math.round(visit.total_points * 100) / 100;
  }

  const visits = [...visitMap.values()];
  visits.sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for) || a.site_name.localeCompare(b.site_name));

  return visits;
}
